import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useClaimDraft } from '../../hooks/useClaimDraft';
import { useClaimValidation } from '../../hooks/useClaimValidation';
import { useLMNLetterPolling } from '../../hooks/useLMNLetterPolling';

import ClaimTopBar from './ClaimTopBar';
import ClaimStepper from './ClaimStepper';
import ValidationPanel from './ValidationPanel';
import ConflictBanner from './ConflictBanner';
import SaveErrorBanner from './SaveErrorBanner';
import ExpertActionBar from './ExpertActionBar';
import ExpertGrid from './ExpertGrid';

import PatientBasicsSection from './sections/PatientBasicsSection';
import SubscriberSection from './sections/SubscriberSection';
import PayerRoutingSection from './sections/PayerRoutingSection';
import ProviderSection from './sections/ProviderSection';
import DiagnosisSection from './sections/DiagnosisSection';
import LineItemsSection from './sections/LineItemsSection';
import DocumentsSection from './sections/DocumentsSection';
import ReviewSection from './sections/ReviewSection';

import NewPatientModal from './modals/NewPatientModal';
import CMS1500PreviewModal from './modals/CMS1500PreviewModal';
import SubmitModal from './modals/SubmitModal';
import LMNModal from './modals/LMNModal';
import DiscardEditsModal from './modals/DiscardEditsModal';
import CreateFromPdfOverlay from './CreateFromPdfOverlay';
import Toast from './shared/Toast';
import Icon from './shared/Icon';
import Btn from './shared/Btn';
import { STEPS, countErrorsForStep } from './STEPS';
import {
  cloneClaim,
  deleteLMNLetter,
  fetchPreviewPdf,
  markClaimSubmitted,
  reopenClaim,
  submitClaim,
} from '../../api/claimsClient';
import { getClaimActionEligibility } from '../../api/claimActionsApi';

const MODE_KEY = 'appealbot_claim_mode';

/** @param {any} value */
function isValidNpi(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length !== 10) return false;
  const payload = `80840${digits.slice(0, 9)}`;
  let checksum = 0;
  [...payload].reverse().forEach((char, index) => {
    let digit = Number(char);
    if (index % 2 === 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    checksum += digit;
  });
  const checkDigit = (10 - (checksum % 10)) % 10;
  return checkDigit === Number(digits[9]);
}

/**
 * @param {{ currentUser?: { oa_onboarded?: boolean, claim_md_onboarded?: boolean }|null }} props
 */
export default function ClaimFlowPage({ currentUser = null }) {
  const { claimId } = useParams();
  const navigate = useNavigate();

  const draft = useClaimDraft(claimId ?? null);
  const validation = useClaimValidation(claimId ?? null);
  const [mode, setMode] = useState(() => {
    // eslint-disable-next-line no-restricted-properties -- non-secret UI mode preference, not auth state
    try { return localStorage.getItem(MODE_KEY) || 'guided'; } catch { return 'guided'; }
  });
  const [stepIdx, setStepIdx] = useState(0);
  const [channelHint, setChannelHint] = useState(/** @type {'office_ally'|'fax'|'export'} */ ('office_ally'));
  /** @type {[Array<{letterId: string, status: string, restartKey?: number}>, any]} */
  const [lmnLetters, setLmnLetters] = useState(/** @type {any} */ ([]));
  const [submitOpen, setSubmitOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [lmnOpen, setLmnOpen] = useState(false);
  const [activeLetterId, setActiveLetterId] = useState(/** @type {string|null} */ (null));
  const [documentsRefreshKey, setDocumentsRefreshKey] = useState(0);
  const [newPatientOpen, setNewPatientOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [createFromPdfOpen, setCreateFromPdfOpen] = useState(false);
  /** @type {[any, any]} */
  const [toast, setToast] = useState(/** @type {any} */ (null));
  const [eligibility, setEligibility] = useState(/** @type {any} */ (null));

  useEffect(() => {
    if (!claimId) return;
    getClaimActionEligibility(claimId)
      .then(setEligibility)
      .catch(() => setEligibility({ eligible: false, reason: 'error' }));
  }, [claimId]);

  const handleDeleteLMN = useCallback(async (letterId) => {
    try {
      await deleteLMNLetter(letterId);
      setLmnLetters((prev) => prev.filter((j) => j.letterId !== letterId));
      if (activeLetterId === letterId) {
        setActiveLetterId(null);
        setLmnOpen(false);
      }
      setDocumentsRefreshKey((k) => k + 1);
      setToast({ severity: 'success', message: 'Letter of Medical Necessity removed.' });
    } catch (/** @type {any} */ e) {
      // LMN views return {"error": "..."}; other endpoints return {"detail": "..."}.
      // Check both to stay convention-compatible (LMNEditorPanel.js:79 same shape).
      const detail =
        e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'error';
      setToast({ severity: 'error', message: `Failed to delete LMN: ${detail}` });
    }
  }, [activeLetterId]);

  useEffect(() => {
    // eslint-disable-next-line no-restricted-properties -- non-secret UI mode preference, not auth state
    try { localStorage.setItem(MODE_KEY, mode); } catch (_e) { /* ignore */ }
  }, [mode]);

  useEffect(() => {
    if (draft.claim?.authoring_mode && draft.claim.authoring_mode !== mode) {
      setMode(draft.claim.authoring_mode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.claim?.authoring_mode]);

  /** @param {string} next */
  const onModeChange = (next) => {
    setMode(next);
    if (draft.editable) draft.setField('authoring_mode', next);
  };

  const clearValidationForEdit = useCallback((/** @type {string} */ path, /** @type {any} */ value) => {
    if (path === 'billing_provider_snapshot.npi' && isValidNpi(value)) {
      validation.clearFieldIssue(path);
    }
    if (path === 'claim_filing_indicator' && value) {
      validation.clearFieldIssue(path);
    }
    if (path === 'service_lines' && Array.isArray(value)) {
      value.forEach((line, index) => {
        const lineNumber = line.line_number || index + 1;
        const clearLineField = (/** @type {string} */ field) => {
          validation.clearFieldIssue(`service_lines[${lineNumber}].${field}`);
          validation.clearFieldIssue(`service_lines[?].${field}`);
        };
        if (line.service_date_from) clearLineField('service_date_from');
        if (line.service_date_to || line.service_date_from) clearLineField('service_date_to');
        if (line.place_of_service) clearLineField('place_of_service');
        if (line.cpt_hcpcs_code) clearLineField('cpt_hcpcs_code');
        if (line.diagnosis_pointer_1) clearLineField('diagnosis_pointer_1');
        if (Number(line.charge_amount) > 0) clearLineField('charge_amount');
        if (Number(line.units) > 0) clearLineField('units');
      });
    }
  }, [validation]);

  const setField = useCallback((/** @type {string} */ path, /** @type {any} */ value) => {
    draft.setField(path, value);
    clearValidationForEdit(path, value);
  }, [draft, clearValidationForEdit]);

  const setMany = useCallback((/** @type {Record<string, any>} */ updates) => {
    draft.setMany(updates);
    Object.entries(updates || {}).forEach(([path, value]) => clearValidationForEdit(path, value));
  }, [draft, clearValidationForEdit]);

  // Manual save (autosave is disabled). Flushes any dirty fields, then returns
  // whether the save succeeded. Continue uses this before validating.
  const saveDraft = useCallback(async () => {
    const needsVisibleDefaultsSaved = !draft.claim?.place_of_service;
    if (!draft.hasDirty && !needsVisibleDefaultsSaved) return true;
    const ok = await draft.flush();
    if (!ok) {
      setToast({ severity: 'error', message: 'Save failed. Resolve the error and try again.' });
    }
    return ok;
  }, [draft]);

  // Continue: save first, then advance.
  // In guided mode, validation runs only when the user clicks Validate or
  // tries to Submit — Continue does not auto-validate. Expert mode keeps the
  // step-blocking behavior since users explicitly chose the dense layout.
  const continueStep = useCallback(async () => {
    const isLast = stepIdx === STEPS.length - 1;
    if (isLast) { setSubmitOpen(true); return; }
    const saved = await saveDraft();
    if (!saved) return;
    if (mode === 'expert') {
      const result = await validation.runValidation();
      if (!result) {
        setToast({ severity: 'error', message: 'Validation could not run. Try again before continuing.' });
        return;
      }
      const blocking = countErrorsForStep(result.errors || [], STEPS[stepIdx]) > 0;
      if (blocking) {
        setToast({ severity: 'error', message: 'Fix the highlighted issues before continuing.' });
        return;
      }
    }
    setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
  }, [stepIdx, validation, saveDraft, mode]);

  /** @param {{ idempotency_key: string, channel: string, fax_number?: string }} body */
  const onSubmitConfirm = async (body) => {
    const updated = await submitClaim(/** @type {string} */ (claimId), /** @type {any} */ (body));
    setSubmitOpen(false);

    if (body.channel === 'export') {
      try {
        const blob = await fetchPreviewPdf(/** @type {string} */ (claimId));
        const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = blobUrl;
        const patientName = draft.claim?.patient
          ? `${draft.claim.patient.first_name || ''}-${draft.claim.patient.last_name || ''}`.replace(/\s+/g, '-').toLowerCase()
          : 'claim';
        link.download = `claim-${patientName}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        setToast({ severity: 'success', message: `PDF downloaded. Confirmation #${updated?.submission_id || ''}` });
      } catch (_err) {
        setToast({ severity: 'warning', message: `Export recorded (confirmation #${updated?.submission_id || ''}) but download failed. Use Preview to download the PDF.` });
      }
    } else {
      // Every non-export channel is ASYNC: this endpoint only queues a worker job.
      // The clearinghouse can still reject the claim seconds later, so "Submitted"
      // with a success severity was showing a green confirmation for claims that
      // never made it — see .planning/debug/claim-md-job-never-reaches-worker.md.
      setToast({
        severity: 'info',
        message: `Queued for ${body.channel}. Confirmation #${updated?.submission_id || ''} — status updates once the clearinghouse responds.`,
      });
    }

    await draft.refetch();
  };

  // Wrapped runValidation that surfaces an outcome toast — the bare hook
  // returns silently on success, which made the Validate button feel dead.
  const runValidationWithToast = useCallback(async () => {
    try {
      const saved = await saveDraft();
      if (!saved) return null;
      const result = await validation.runValidation();
      if (!result) {
        setToast({ severity: 'error', message: 'Validation could not run.' });
        return result;
      }
      const errs = result.errors?.length || 0;
      const warns = result.warnings?.length || 0;
      if (errs === 0 && warns === 0) {
        setToast({ severity: 'success', message: 'Validation passed — claim is ready to submit.' });
      } else if (errs === 0) {
        setToast({ severity: 'info', message: `${warns} warning${warns === 1 ? '' : 's'} — review before submit.` });
      } else {
        setToast({ severity: 'error', message: `${errs} issue${errs === 1 ? '' : 's'} found. See the panel.` });
      }
      return result;
    } catch (e) {
      setToast({ severity: 'error', message: 'Validation request failed. Try again.' });
      return null;
    }
  }, [validation, saveDraft]);

  const onCloneAndEdit = async () => {
    const fresh = await cloneClaim(/** @type {string} */ (claimId));
    if (fresh?.id) navigate(`/claims/${fresh.id}`);
  };

  const onReopenForEditing = async () => {
    if (!window.confirm('Reopen this claim for editing? This will not recall any previously sent submissions.')) return;
    try {
      await reopenClaim(/** @type {string} */ (claimId));
      await draft.refetch();
      setToast({ severity: 'success', message: 'Claim reopened for editing.' });
    } catch (err) {
      if (err?.response?.status === 409) {
        setToast({ severity: 'error', message: 'Claim state changed — refreshing.' });
        await draft.refetch();
      } else {
        setToast({ severity: 'error', message: 'Could not reopen claim. Try again.' });
      }
    }
  };

  const onMarkAsSubmitted = async () => {
    if (!window.confirm('Mark this claim as submitted without sending through AppealBot?')) return;
    try {
      await markClaimSubmitted(/** @type {string} */ (claimId));
      await draft.refetch();
      setToast({ severity: 'success', message: 'Claim marked as submitted.' });
    } catch (err) {
      if (err?.response?.status === 409) {
        setToast({ severity: 'error', message: 'Claim state changed — refreshing.' });
        await draft.refetch();
      } else {
        setToast({ severity: 'error', message: 'Could not mark claim as submitted. Try again.' });
      }
    }
  };

  const onReset = async () => {
    if (!window.confirm(
      'Start a new claim?\n\nThis abandons the current draft (it stays in your claims list) '
      + 'and opens a blank claim. It does NOT revert your edits on this claim — use '
      + '"Discard edits" for that.',
    )) return;
    const fresh = await draft.resetDraft();
    if (fresh?.id) navigate(`/claims/${fresh.id}`);
  };

  // Discard local pending edits and reload from server. Only meaningful when
  // there's a dirty buffer (i.e. autosave hasn't fired yet).
  const onDiscardLocal = useCallback(() => {
    setDiscardOpen(true);
  }, []);

  if (!draft.claim) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--ab-fg-subtle)' }}>Loading…</div>;
  }

  const claim = draft.claim;
  const errorCount = validation.result?.errors?.length || 0;
  const warningCount = validation.result?.warnings?.length || 0;
  const errorsList = validation.result?.errors || [];

  const requestSubmit = async () => {
    if (validation.validating) {
      setToast({ severity: 'info', message: 'Validation is still running.' });
      return;
    }
    // Submit requires the latest claim state on the server — flush any dirty
    // edits first since autosave is disabled.
    const saved = await saveDraft();
    if (!saved) return;
    // Always run a fresh validation pass before opening the SubmitModal.
    // The cached validation.result may be stale (or null on first submit
    // attempt with empty fields), and we must NEVER let an empty/incomplete
    // claim reach the channel-picker modal.
    const result = await validation.runValidation();
    const freshErrorCount = result?.errors?.length ?? errorCount;
    if (!result || freshErrorCount > 0) {
      setToast({
        severity: 'error',
        message: `Resolve ${freshErrorCount} issue${freshErrorCount === 1 ? '' : 's'} before submitting.`,
      });
      return;
    }
    setSubmitOpen(true);
  };

  const renderStep = () => {
    const stepKey = STEPS[stepIdx].key;
    if (stepKey === 'basics') {
      return <PatientBasicsSection
        claim={claim} setField={setField} setMany={setMany}
        selectPatient={draft.selectPatient} onCreateNew={() => setNewPatientOpen(true)}
        editable={draft.editable} errorsByField={validation.errorsByField}
      />;
    }
    if (stepKey === 'insurance') {
      return <>
        <SubscriberSection claim={claim} setField={setField} editable={draft.editable} errorsByField={validation.errorsByField} />
        <PayerRoutingSection
          claim={claim} setField={setField} setMany={setMany}
          editable={draft.editable} errorsByField={validation.errorsByField}
          channelHint={channelHint} setChannelHint={/** @type {(ch: string) => void} */ (setChannelHint)}
        />
      </>;
    }
    if (stepKey === 'provider') {
      return <ProviderSection
        claim={claim} setField={setField} setMany={setMany}
        editable={draft.editable} errorsByField={validation.errorsByField}
        onAlert={(/** @type {string} */ m, /** @type {string|undefined} */ s) => setToast({ severity: s || 'info', message: m })}
      />;
    }
    if (stepKey === 'services') {
      return <>
        <DiagnosisSection claim={claim} setField={setField} editable={draft.editable} errorsByField={validation.errorsByField} />
        <LineItemsSection claim={claim} setField={setField} editable={draft.editable} errorsByField={validation.errorsByField} />
      </>;
    }
    if (stepKey === 'docs') {
      return <DocumentsSection
        claim={claim} editable={draft.editable}
        onAlert={(/** @type {string} */ m, /** @type {string|undefined} */ s) => setToast({ severity: s || 'info', message: m })}
        onGenerateLMN={() => { setActiveLetterId(null); setLmnOpen(true); }}
        onOpenLetter={(/** @type {string} */ letterId) => { setActiveLetterId(letterId); setLmnOpen(true); }}
        lmnLetters={lmnLetters}
        onDocumentsChanged={() => draft.refetch()}
        documentsRefreshKey={documentsRefreshKey}
        onDeleteLMN={handleDeleteLMN}
      />;
    }
    if (stepKey === 'review') {
      return <ReviewSection
        claim={claim} editable={draft.editable}
        onJumpToStep={setStepIdx}
        onPreview={() => setPreviewOpen(true)}
        onSubmit={requestSubmit}
        validationErrorCount={errorCount}
        documentsRefreshKey={documentsRefreshKey}
      />;
    }
    return null;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--ab-bg-alt)' }}>
      <ClaimTopBar
        patientName={claim.patient ? `${claim.patient.first_name || ''} ${claim.patient.last_name || ''}`.trim() : ''}
        draftStatus={/** @type {'draft'|'validated'|'submitted'} */ (draft.draftStatus)}
        saveState={/** @type {'idle'|'pending'|'saving'|'saved'|'error'} */ (draft.saveState)}
        lastSavedAt={draft.lastSavedAt}
        mode={/** @type {'guided'|'expert'} */ (mode)} onModeChange={onModeChange}
        onReset={onReset}
        onDiscardLocal={onDiscardLocal}
        onCloneAndEdit={onCloneAndEdit}
        onReopenForEditing={draft.draftStatus === 'submitted' ? onReopenForEditing : null}
        onRetrySave={draft.flush}
        editable={draft.editable}
        hasDirty={draft.hasDirty}
        onCreateFromPdf={() => setCreateFromPdfOpen(true)}
      />
      {mode === 'guided' && (
        <ClaimStepper
          currentStepIdx={stepIdx}
          onStepClick={setStepIdx}
          errors={errorsList}
        />
      )}
      <ConflictBanner
        conflict={draft.conflict}
        onReload={() => draft.resolveConflict('reload')}
        onForce={() => draft.resolveConflict('force')}
      />
      <SaveErrorBanner saveError={draft.saveError} onRetry={draft.flush} />
      {eligibility && !eligibility.eligible && (
        <div style={{
          background: 'var(--ab-info-soft)',
          borderLeft: '4px solid var(--ab-info)',
          color: 'var(--ab-fg)',
          padding: '10px 16px',
          fontSize: 13,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          margin: '8px 16px 0',
        }}>
          <Icon name="info" size={14} aria-hidden="true" />
          Optum claim action data is not available yet. Refresh Optum claim details before creating a reconsideration.
        </div>
      )}
      {eligibility?.eligible && (
        <div style={{ padding: '8px 16px' }}>
          <Btn kind="primary" onClick={() => navigate(`/claims/${claimId}/appeal`)}>Create Reconsideration</Btn>
        </div>
      )}
      {claim.has_submission_history && claim.draft_status !== 'submitted' && (
        <div role="alert" style={{
          background: 'var(--ab-pending-soft)',
          borderLeft: '4px solid var(--ab-pending-ring)',
          color: 'var(--ab-pending)',
          padding: '10px 16px',
          fontSize: 13,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <Icon name="alert-triangle" size={14} aria-hidden="true" />
          This claim was previously submitted. Review submission history before sending again.
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, gap: 24, padding: 24 }}>
        <main style={{ flex: 1, minWidth: 0 }}>
          {mode === 'expert' ? (
            <ExpertGrid
              claim={claim}
              setField={setField} setMany={setMany}
              selectPatient={draft.selectPatient}
              onCreatePatient={() => setNewPatientOpen(true)}
              editable={draft.editable}
              errorsByField={validation.errorsByField}
            />
          ) : (
            <>
              {renderStep()}
              {STEPS[stepIdx].key !== 'review' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
                  <Btn kind="secondary" icon="arrow-left" disabled={stepIdx === 0}
                    onClick={() => setStepIdx((i) => Math.max(0, i - 1))}>
                    Previous
                  </Btn>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn
                      kind="secondary"
                      icon="save"
                      onClick={saveDraft}
                      disabled={!draft.editable || !draft.hasDirty || draft.saveState === 'saving'}
                    >
                      {draft.saveState === 'saving' ? 'Saving…' : 'Save'}
                    </Btn>
                    <Btn kind="primary" iconRight="arrow-right" onClick={continueStep}>Continue</Btn>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
        <ValidationPanel
          result={validation.result}
          validationError={validation.validationError}
          errors={errorsList}
          validating={validation.validating}
          editable={draft.editable}
          onRunValidation={runValidationWithToast}
          onPreview={() => setPreviewOpen(true)}
          onSubmit={requestSubmit}
          onJumpToStep={setStepIdx}
          draftStatus={claim.draft_status}
          onMarkAsSubmitted={onMarkAsSubmitted}
        />
      </div>

      {mode === 'expert' && (
        <ExpertActionBar
          editable={draft.editable}
          errorCount={errorCount}
          warningCount={warningCount}
          validating={validation.validating}
          onValidate={runValidationWithToast}
          onPreview={() => setPreviewOpen(true)}
          onSubmit={requestSubmit}
          onSave={saveDraft}
          hasDirty={draft.hasDirty}
          saveState={draft.saveState}
        />
      )}

      <NewPatientModal
        open={newPatientOpen}
        onClose={() => setNewPatientOpen(false)}
        onCreated={(/** @type {any} */ patient) => { draft.selectPatient(patient); setNewPatientOpen(false); setToast({ severity: 'success', message: 'Patient added & linked' }); }}
      />
      <CMS1500PreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} claimId={claimId ?? null} />
      <SubmitModal
        open={submitOpen} onClose={() => setSubmitOpen(false)}
        currentUser={currentUser}
        payerId={claim.payer_snapshot?.payer_id || null}
        claimId={claimId ?? null}
        defaultChannel={channelHint}
        onConfirm={onSubmitConfirm}
      />
      <LMNModal
        open={lmnOpen}
        onClose={() => { setLmnOpen(false); setActiveLetterId(null); }}
        claimId={/** @type {string} */ (claimId)}
        letterId={activeLetterId}
        defaultPrompt={claim.notes || ''}
        onDeleteLMN={handleDeleteLMN}
        onJobStarted={(/** @type {string} */ letterId) => {
          setLmnLetters((/** @type {any[]} */ prev) => [...prev, { letterId, status: 'generating', restartKey: 0 }]);
          // Keep the modal open and flip it into editor phase for the new letter
          // (LMNModal.inEditor = !!activeLetterId). The background lmnLetters poller
          // above still handles the attach if the user closes before it finishes.
          setActiveLetterId(letterId);
          setToast({ severity: 'info', message: 'LMN generation started — editing opens now; we\'ll attach it when it\'s ready.' });
        }}
        onRegenerateStarted={(/** @type {string} */ letterId) => {
          // Ensure the letter is in the polling array; bump restartKey so the
          // polling hook restarts after the same-letter status transition.
          // Mark isRegenerate so the terminal toast says "updated" not "attached".
          setLmnLetters((/** @type {any[]} */ prev) => {
            if (prev.some((x) => x.letterId === letterId)) {
              return prev.map((x) =>
                x.letterId === letterId
                  ? { ...x, restartKey: (x.restartKey || 0) + 1, isRegenerate: true }
                  : x
              );
            }
            return [...prev, { letterId, status: 'generating', restartKey: 0, isRegenerate: true }];
          });
          setToast({ severity: 'info', message: 'Regenerating LMN — we\'ll let you know when it\'s ready.' });
        }}
        onSaveCompleted={() => {
          setDocumentsRefreshKey((k) => k + 1);
          setToast({ severity: 'success', message: 'LMN saved.' });
        }}
      />
      <DiscardEditsModal
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        onConfirm={async () => { setDiscardOpen(false); await draft.refetch(); }}
      />
      <CreateFromPdfOverlay
        open={createFromPdfOpen}
        onClose={() => setCreateFromPdfOpen(false)}
        onCommit={(newClaimId) => {
          setCreateFromPdfOpen(false);
          if (newClaimId) navigate(`/claims/${newClaimId}`);
        }}
      />
      {lmnLetters.map((/** @type {any} */ j) => (
        <LMNPollingHook
          key={j.letterId}
          letterId={j.letterId}
          claimId={/** @type {string} */ (claimId)}
          restartKey={j.restartKey || 0}
          onTerminal={(status) => {
            const wasRegenerate = !!j.isRegenerate;
            setLmnLetters((/** @type {any[]} */ prev) => prev.filter((x) => x.letterId !== j.letterId));
            setDocumentsRefreshKey((k) => k + 1);
            if (status === 'generated') {
              draft.refetch();
              setToast({
                severity: 'success',
                message: wasRegenerate ? 'LMN updated.' : 'LMN attached to claim.',
              });
            } else if (status === 'failed') {
              setToast({
                severity: 'error',
                message: wasRegenerate
                  ? 'LMN regeneration failed.'
                  : 'LMN generation failed.',
              });
            }
          }}
        />
      ))}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

/**
 * Tiny adapter so each polling instance can be unmounted cleanly.
 * @param {{ letterId: string, claimId: string, restartKey?: number, onTerminal: (status: string) => void }} props
 */
function LMNPollingHook({ letterId, claimId, restartKey, onTerminal }) {
  const { status } = useLMNLetterPolling(letterId, claimId, restartKey || 0);
  useEffect(() => {
    if (status === 'generated' || status === 'failed') onTerminal(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);
  return null;
}
